import React, { useEffect, useState, useCallback, useMemo } from "react";
import { FaTimes } from "react-icons/fa";
import { IoChatboxEllipsesOutline } from "react-icons/io5";
import { createPanel, getPanelbyParticipant, getPanelsByAdminUnitOrgUnit, getPanelsByAdminUnit } from "app/apis/panels";

import { CIVIL_CASE_DIRECTOR_ROLE, CRIME_CASE_DIRECTOR_ROLE, CIVIL_CASE_TEAMLEADER_ROLE, PRIORITY_LEVELS, WOREDA_ATTORNEY_OFFICE_HEAD, ZONAL_ATTORNEY_OFFICE_HEAD } from "app/utils/const";
import { useAppContext } from "app/contexts/AppContext";
import MainChat from "./main_chat";
import RightSidebar from "./right_sidebar";
import PanelMembers from "./panel_sidebar/panel_members";
import Pagination from "../common_component/Pagination";

import { useTranslation } from "react-i18next";
import { FinovaDatePicker } from "ethiopian-calendar-finova";

const ITEMS_PER_PAGE = 10;
const INITIAL_PANEL_DATA = {
  org_unit: "",
  admin_unit: "",
  starts_at: "",
  agenda: "",
  case_id: "",
  panel_title: "",
  priority_level: "NORMAL",
};

const PanelChatUI = () => {
  const { t } = useTranslation();
  const employee_id = sessionStorage.getItem('employee_id');
  const current_user_role = sessionStorage.getItem('roles');
  const admin_unit = sessionStorage.getItem('admin_unit');
  const org_unit = sessionStorage.getItem('org_unit');

  // State management
  const { selectedPanel, setSelectedPanel } = useAppContext();
  const [isLoading, setIsLoading] = useState(true);
  const [panelsDetail, setPanelsDetail] = useState([]);
  const [selectableCase, setSelectableCase] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [newPanelData, setNewPanelData] = useState(INITIAL_PANEL_DATA);
  const [error, setError] = useState(null);
  const [change, setChange] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Memoized role check
  const isTeamLeader = useMemo(() =>
    current_user_role?.includes(CIVIL_CASE_TEAMLEADER_ROLE),
    [current_user_role]
  );

  const isDirector = useMemo(() =>
    current_user_role?.includes(CIVIL_CASE_DIRECTOR_ROLE) ||
    current_user_role?.includes(CRIME_CASE_DIRECTOR_ROLE),
    [current_user_role]
  );
  const isHead = useMemo(() =>
    current_user_role?.includes(ZONAL_ATTORNEY_OFFICE_HEAD) ||
    current_user_role?.includes(WOREDA_ATTORNEY_OFFICE_HEAD),
    [current_user_role]
  );

  // Memoized filtered cases for dropdown
  const filteredCases = useMemo(() =>
    selectableCase?.filter(caseItem =>
      caseItem?.registration_number && caseItem.registration_number !== "Not Assigned"
    ),
    [selectableCase]
  );

  // Find the selected case for prefix
  const selectedCase = useMemo(
    () => filteredCases.find((c) => c.uu_id === newPanelData.case_id),
    [filteredCases, newPanelData.case_id]
  );

  // Event handlers
  const handlePanelClick = (panel) => {
    setSelectedPanel(panel);
  };

  // Fetch panels with pagination
  const fetchPanels = useCallback(async () => {
    if (!employee_id) return;

    try {
      setIsLoading(true);
      let response;

      if (isDirector) {
        response = await getPanelsByAdminUnitOrgUnit(
          org_unit,
          admin_unit,
          currentPage,
          ITEMS_PER_PAGE,
          searchTerm
        );
      }
      else if (isHead) {
        response = await getPanelsByAdminUnit(
          admin_unit,
          currentPage,
          ITEMS_PER_PAGE,
          searchTerm
        );
      } else {
        response = await getPanelbyParticipant(
          employee_id,
          currentPage,
          ITEMS_PER_PAGE,
          searchTerm
        );
      }

      setPanelsDetail(response.data.panels || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || 0);
    } catch (error) {
      console.error("Error fetching panels:", error);
    } finally {
      setIsLoading(false);
    }
  }, [employee_id, currentPage, searchTerm, isDirector, selectedCase, admin_unit]);

  useEffect(() => {
    if (employee_id && (isDirector || isHead || employee_id)) {
      fetchPanels();
    }
  }, [fetchPanels]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPanelData(prev => ({ ...prev, [name]: value }));
  };

  const handlePanelTitleChange = (e) => {
    const words = e.target.value.split(/\s+/).filter(Boolean);
    if (words.length <= 6) {
      handleInputChange(e);
    }
  };

  const handleCreatePanel = useCallback(async () => {
    if (!newPanelData.case_id) {
      setError(t("Case ID is required."));
      return;
    }

    if (!newPanelData.panel_title) {
      setError(t("Panel Title is required."));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const panelResponse = await createPanel({
        admin_unit: newPanelData?.admin_unit,
        starts_at: newPanelData?.starts_at,
        case_id: newPanelData?.case_id,
        agenda: newPanelData?.agenda,
        priority: newPanelData?.priority_level,
        panel_title: newPanelData?.panel_title,
        created_by: employee_id,
        admin_unit: admin_unit,
        org_unit: org_unit
      });

      setNewPanelData(INITIAL_PANEL_DATA);
      setIsPopupOpen(false);
      setCurrentPage(1);
      await fetchPanels();
    } catch (error) {
      console.error("Error creating panel:", error);
      setError(error?.response?.data?.message || error?.message || t("Failed to create panel. Please try again."));
    } finally {
      setIsLoading(false);
    }
  }, [newPanelData, employee_id, admin_unit, fetchPanels]);

  const handleCloseMeeting = async () => {
    if (!selectedPanel || !selectedPanel.uu_id) return;

    try {
      setIsLoading(true);
      await fetchPanels();
      setSelectedPanel(null);
    } catch (error) {
      console.error("Error closing panel:", error);
      setError(error?.response?.data?.message || error?.message || t("Failed to close panel. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleAddNewPanel = () => {
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setNewPanelData(INITIAL_PANEL_DATA);
    setError(null);
  };

  return (
    <div className="flex h-[80vh] bg-white border rounded-lg shadow-lg overflow-hidden">
      {/* Sidebar */}
      <div className="w-[25vw] bg-white border-r flex flex-col">
        {/* Header with search and button */}
        <div className="sticky top-0 bg-white p-4 pb-3 z-10 border-b">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold flex items-center">
              <IoChatboxEllipsesOutline className="text-xl mr-2" /> {t("Case List")}
            </h2>
          </div>

          <div className="flex space-x-2">
            <div className="flex-1">
              <input
                type="text"
                placeholder={t("search case panel")}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            {isTeamLeader && (
              <button
                className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors whitespace-nowrap text-sm"
                onClick={handleAddNewPanel}
                disabled={isLoading}
              >
                + {t("New Panel")}
              </button>
            )}
          </div>
        </div>

        {/* Scrollable panel list */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="space-y-2">
              {panelsDetail?.length > 0 ? (
                panelsDetail?.map((data) => (
                  <div
                    key={data.uu_id}
                    className={`p-3 rounded flex justify-between items-center cursor-pointer transition-colors ${selectedPanel?.uu_id === data?.uu_id
                      ? "bg-blue-200"
                      : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    onClick={() => handlePanelClick(data)}
                  >
                    <span className="truncate text-sm">
                      {data?.case + " - " + data?.panel_title}
                      {data.status === "CLOSED" && (
                        <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                          {t("Closed")}
                        </span>
                      )}
                    </span>
                    <PanelMembers id={data?.uu_id} />
                  </div>
                ))
              ) : (
                <div className="p-3 text-center text-gray-500 text-sm">
                  {t("No panels found")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Main Chat Section */}
      <MainChat
        onCloseMeeting={isDirector ? handleCloseMeeting : null}
        canCloseMeeting={isDirector}
        change={change}
      />

      {/* Right Sidebar */}
      <RightSidebar />

      {/* New Panel Popup */}
      {isPopupOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{t("create panel")}</h2>
              <button
                onClick={handleClosePopup}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Case Reg. No.")}
                </label>
                <input
                  type="text"
                  name="case_id"
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  value={newPanelData.case_id}
                  onChange={handleInputChange}
                  placeholder={t("Enter case registration number")}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Panel Title")}
                </label>
                <div className="flex">
                  <input
                    type="text"
                    name="panel_title"
                    className="flex-1 rounded-r border border-l-0 p-2 text-sm"
                    value={newPanelData.panel_title}
                    onChange={handlePanelTitleChange}
                    required
                    placeholder={t("Enter panel title (max 6 words)")}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Start Time")}
                </label>
                <FinovaDatePicker
                  name="starts_at"
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  value={newPanelData.starts_at}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Agenda")}
                </label>
                <textarea
                  name="agenda"
                  placeholder={t("Enter Agenda")}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  value={newPanelData.agenda}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Priority Level")}
                </label>
                <select
                  name="priority_level"
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  value={newPanelData.priority_level}
                  onChange={handleInputChange}
                >
                  {Object.values(PRIORITY_LEVELS).map((priority) => (
                    <option key={priority.value} value={priority.value}>
                      {t(priority.label)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <div className="mt-3 text-red-500 text-sm">{error}</div>
            )}

            <button
              className="w-full mt-6 p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
              onClick={handleCreatePanel}
              disabled={isLoading}
            >
              {isLoading ? t("Creating...") : t("create panel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelChatUI;